import { useMsal } from '@azure/msal-react';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { alpha, Box, Button, Paper, Stack, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { loginRequest } from './msalConfig';
import type { ReactNode } from 'react';
import { isAuthBypassEnabled } from './authSettings';
import { useTranslation } from 'react-i18next';

/** Renders children only when authenticated; otherwise shows a sign-in screen. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  if (isAuthBypassEnabled) {
    return <>{children}</>;
  }

  return (
    <>
      <AuthenticatedTemplate>
        {children}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 4 },
            background: (theme) =>
              theme.palette.mode === 'light'
                ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${theme.palette.background.default} 42%, #fff 100%)`
                : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${theme.palette.background.default} 55%)`,
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 420,
              height: 420,
              borderRadius: '50%',
              top: -230,
              right: -130,
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3.5, sm: 5 },
              maxWidth: 460,
              width: '100%',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: (theme) => `0 24px 64px ${alpha(theme.palette.common.black, theme.palette.mode === 'light' ? 0.12 : 0.35)}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '0 0 auto',
                height: 144,
                background: (theme) =>
                  `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.13)}, ${alpha(theme.palette.primary.main, 0.035)})`,
              },
            }}
          >
            <Stack spacing={3} alignItems="center" textAlign="center" sx={{ position: 'relative' }}>
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 3,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                }}
              >
                <Box
                  component="img"
                  src="/brand/allphi-logo.png"
                  alt={t('app.title')}
                  sx={{ width: 62, height: 62, objectFit: 'contain' }}
                />
              </Box>
              <Stack spacing={1}>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.65rem', sm: '1.9rem' }, fontWeight: 700 }}>
                  {t('auth.title')}
                </Typography>
                <Typography color="text.secondary">
                  {t('auth.subtitle')}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<LoginIcon />}
                onClick={() => instance.loginRedirect(loginRequest)}
                sx={{ py: 1.25, borderRadius: 2 }}
              >
                {t('auth.signIn')}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </UnauthenticatedTemplate>
    </>
  );
}
